import { z } from 'zod';
import { appBillingDTOSchema, billingPayerDTOSchema, catalogPlanDTOSchema, hostedSessionDTOSchema, nextInvoiceDTOSchema, paymentMethodStatusDTOSchema, pendingChargedChangeDTOSchema, planFeatureDTOSchema, planSummaryDTOSchema, pricePreviewDTOSchema, tenantEntitlementAdminDTOSchema, tenantEntitlementsDTOSchema, tenantOverrideDTOSchema, } from '@instanexus/contracts';
import { dataEnvelopeSchema, WEB_API_PREFIX } from '../transport.js';
const entitlementsSchema = dataEnvelopeSchema(tenantEntitlementsDTOSchema);
const plansSchema = dataEnvelopeSchema(z.array(planSummaryDTOSchema));
const catalogSchema = dataEnvelopeSchema(z.array(catalogPlanDTOSchema));
const hostedSessionSchema = dataEnvelopeSchema(hostedSessionDTOSchema);
const planFeatureSchema = dataEnvelopeSchema(planFeatureDTOSchema);
const tenantEntitlementAdminSchema = dataEnvelopeSchema(tenantEntitlementAdminDTOSchema);
const tenantOverrideSchema = dataEnvelopeSchema(tenantOverrideDTOSchema);
const pricePreviewSchema = dataEnvelopeSchema(pricePreviewDTOSchema);
const appBillingSchema = dataEnvelopeSchema(appBillingDTOSchema);
const upcomingInvoiceSchema = dataEnvelopeSchema(nextInvoiceDTOSchema.nullable());
const paymentMethodStatusSchema = dataEnvelopeSchema(paymentMethodStatusDTOSchema);
const paymentMethodStatusOrNullSchema = dataEnvelopeSchema(paymentMethodStatusDTOSchema.nullable());
const pendingChargeSchema = dataEnvelopeSchema(pendingChargedChangeDTOSchema.nullable());
const ENTITLEMENTS_PATH = `${WEB_API_PREFIX}/billing/entitlements`;
const PLANS_PATH = `${WEB_API_PREFIX}/billing/plans`;
const CATALOG_PATH = `${WEB_API_PREFIX}/billing/catalog`;
const CHECKOUT_SESSION_PATH = `${WEB_API_PREFIX}/billing/checkout-session`;
const PORTAL_SESSION_PATH = `${WEB_API_PREFIX}/billing/portal-session`;
const FREE_SUBSCRIPTION_PATH = `${WEB_API_PREFIX}/billing/subscription/free`;
const APPS_PATH = `${WEB_API_PREFIX}/billing/apps`;
const PREVIEW_PATH = `${WEB_API_PREFIX}/billing/preview`;
const PLAN_CHANGE_PATH = `${WEB_API_PREFIX}/billing/subscription/change`;
const UPCOMING_INVOICE_PATH = `${WEB_API_PREFIX}/billing/upcoming-invoice`;
const billingPayerSchema = dataEnvelopeSchema(billingPayerDTOSchema);
const PAYER_PATH = `${WEB_API_PREFIX}/billing/payer`;
const PAYMENT_METHOD_PATH = `${WEB_API_PREFIX}/billing/payment-method`;
const PAYMENT_METHOD_SESSION_PATH = `${WEB_API_PREFIX}/billing/payment-method/session`;
const PAYMENT_RETRY_PATH = `${WEB_API_PREFIX}/billing/payment/retry`;
const PENDING_CHARGE_PATH = `${WEB_API_PREFIX}/billing/pending-charge`;
const appPath = (app) => `${WEB_API_PREFIX}/billing/apps/${encodeURIComponent(app)}`;
const planFeaturePath = (planCode, featureKey) => `${WEB_API_PREFIX}/billing/plans/${encodeURIComponent(planCode)}/features/${encodeURIComponent(featureKey)}`;
const tenantEntitlementsPath = (tenantId) => `${WEB_API_PREFIX}/billing/tenants/${encodeURIComponent(tenantId)}/entitlements`;
const tenantOverridePath = (tenantId, featureKey) => `${WEB_API_PREFIX}/billing/tenants/${encodeURIComponent(tenantId)}/overrides/${encodeURIComponent(featureKey)}`;
const tenantPricePreviewPath = (tenantId) => `${WEB_API_PREFIX}/billing/tenants/${encodeURIComponent(tenantId)}/price-preview`;
const tenantAppTrialPath = (tenantId, app) => `${WEB_API_PREFIX}/billing/tenants/${encodeURIComponent(tenantId)}/apps/${encodeURIComponent(app)}/trial`;
const tenantAppPlanPath = (tenantId, app) => `${WEB_API_PREFIX}/billing/tenants/${encodeURIComponent(tenantId)}/apps/${encodeURIComponent(app)}/plan`;
export function createBillingResource(transport) {
    return {
        async entitlements() {
            const envelope = await transport.request({
                path: ENTITLEMENTS_PATH,
                schema: entitlementsSchema,
            });
            return envelope.data;
        },
        async plans() {
            const envelope = await transport.request({ path: PLANS_PATH, schema: plansSchema });
            return envelope.data;
        },
        async catalog() {
            const envelope = await transport.request({ path: CATALOG_PATH, schema: catalogSchema });
            return envelope.data;
        },
        async startCheckout(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: CHECKOUT_SESSION_PATH,
                schema: hostedSessionSchema,
                body: input,
            });
            return envelope.data;
        },
        async previewPriceChange(change) {
            const envelope = await transport.request({
                method: 'POST',
                path: PREVIEW_PATH,
                schema: pricePreviewSchema,
                body: change,
            });
            return envelope.data;
        },
        async upcomingInvoice() {
            const envelope = await transport.request({ path: UPCOMING_INVOICE_PATH, schema: upcomingInvoiceSchema });
            return envelope.data;
        },
        async previewOperatorChange(tenantId, change) {
            const envelope = await transport.request({
                method: 'POST',
                path: tenantPricePreviewPath(tenantId),
                schema: pricePreviewSchema,
                body: change,
            });
            return envelope.data;
        },
        async openPortal() {
            const envelope = await transport.request({
                method: 'POST',
                path: PORTAL_SESSION_PATH,
                schema: hostedSessionSchema,
            });
            return envelope.data;
        },
        async paymentMethod() {
            const envelope = await transport.request({ path: PAYMENT_METHOD_PATH, schema: paymentMethodStatusOrNullSchema });
            return envelope.data;
        },
        async payer() {
            const envelope = await transport.request({ path: PAYER_PATH, schema: billingPayerSchema });
            return envelope.data;
        },
        async openPaymentMethodUpdate(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: PAYMENT_METHOD_SESSION_PATH,
                schema: hostedSessionSchema,
                body: input,
            });
            return envelope.data;
        },
        async retryPayment(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: PAYMENT_RETRY_PATH,
                schema: paymentMethodStatusSchema,
                body: input,
            });
            return envelope.data;
        },
        async pendingCharge() {
            const envelope = await transport.request({ path: PENDING_CHARGE_PATH, schema: pendingChargeSchema });
            return envelope.data;
        },
        async cancelPendingCharge() {
            await transport.request({ method: 'DELETE', path: PENDING_CHARGE_PATH, schema: z.undefined() });
        },
        async selectFreePlan() {
            const envelope = await transport.request({
                method: 'POST',
                path: FREE_SUBSCRIPTION_PATH,
                schema: entitlementsSchema,
            });
            return envelope.data;
        },
        async addApp(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: APPS_PATH,
                schema: entitlementsSchema,
                body: input,
            });
            return envelope.data;
        },
        async changePlan(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: PLAN_CHANGE_PATH,
                schema: entitlementsSchema,
                body: input,
            });
            return envelope.data;
        },
        async cancelPlanChange(input) {
            const envelope = await transport.request({
                method: 'DELETE',
                path: PLAN_CHANGE_PATH,
                schema: entitlementsSchema,
                body: input,
            });
            return envelope.data;
        },
        async cancelApp(app, input) {
            const envelope = await transport.request({
                method: 'DELETE',
                path: appPath(app),
                schema: entitlementsSchema,
                body: input,
            });
            return envelope.data;
        },
        async resumeApp(app, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${appPath(app)}/resume`,
                schema: entitlementsSchema,
                body: input,
            });
            return envelope.data;
        },
        async getAppBilling(app) {
            const envelope = await transport.request({ path: appPath(app), schema: appBillingSchema });
            return envelope.data;
        },
        async previewAppSubscriptionChanges(app, changes) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${appPath(app)}/changes/preview`,
                schema: pricePreviewSchema,
                body: changes,
            });
            return envelope.data;
        },
        async commitAppSubscriptionChanges(app, input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${appPath(app)}/changes`,
                schema: appBillingSchema,
                body: input,
            });
            return envelope.data;
        },
        async updatePlanFeature(planCode, featureKey, input) {
            const envelope = await transport.request({
                method: 'PATCH',
                path: planFeaturePath(planCode, featureKey),
                schema: planFeatureSchema,
                body: input,
            });
            return envelope.data;
        },
        async tenantEntitlements(tenantId) {
            const envelope = await transport.request({
                path: tenantEntitlementsPath(tenantId),
                schema: tenantEntitlementAdminSchema,
            });
            return envelope.data;
        },
        async setTenantOverride(tenantId, featureKey, input) {
            const envelope = await transport.request({
                method: 'PUT',
                path: tenantOverridePath(tenantId, featureKey),
                schema: tenantOverrideSchema,
                body: input,
            });
            return envelope.data;
        },
        async clearTenantOverride(tenantId, featureKey) {
            await transport.request({
                method: 'DELETE',
                path: tenantOverridePath(tenantId, featureKey),
                schema: z.undefined(),
            });
        },
        async grantAppTrial(tenantId, app, input) {
            const envelope = await transport.request({
                method: 'PUT',
                path: tenantAppTrialPath(tenantId, app),
                schema: tenantEntitlementAdminSchema,
                body: input,
            });
            return envelope.data;
        },
        async assignAppPlan(tenantId, app, input) {
            const envelope = await transport.request({
                method: 'PUT',
                path: tenantAppPlanPath(tenantId, app),
                schema: tenantEntitlementAdminSchema,
                body: input,
            });
            return envelope.data;
        },
    };
}
//# sourceMappingURL=billing.js.map